/**
 * Seafloor — the coast itself: beach, lagoon, reef flat, fore-reef and drop-off, plus current
 * ripples and projected caustics. This plane is most of the screen at a 62-degree pitch, so
 * it carries both the motion parallax the movement model is judged against and the depth read.
 *
 * The bed field lives in world/Coast.ts and is imported as GLSL, not copied — the CPU mirror
 * sits directly beneath it in that file so the two cannot drift apart unnoticed.
 *
 * The mesh FOLLOWS the player along X and is fixed in Z. That is the whole trick behind an
 * endless coast: every term of the field is analytic, so a moving window over it has no seam,
 * no tile and no wrap. Snapping the window to whole quads is what stops the vertices crawling.
 */
import * as THREE from 'three';
import { OCEAN, WATER } from '../data/tuning.ts';
import { applyLure, LURE_GLSL, lureUniforms, type LureState } from './LureLight.ts';
import { COAST_GLSL } from '../world/Coast.ts';
import { TOKENS, hexToLinear, mixRgb, shade, type Rgb } from '../voxel/Palette.ts';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

function v3(c: Rgb): THREE.Vector3 {
  return new THREE.Vector3(c[0], c[1], c[2]);
}

function sunLightDir(): THREE.Vector3 {
  const d = new THREE.Vector3(WATER.sunDir[0], WATER.sunDir[1], WATER.sunDir[2]).normalize();
  if (d.y < 0) d.negate();
  return d;
}

/** along-shore extent of the moving window, u */
const SPAN_X = 660;
/* Cross-shore extent. Sized by sight line, not by the profile: the top of the frame is 28
   degrees below horizontal (62 pitch, 68 FOV), so from a camera 50 u up over the drop-off the
   ground is visible to about 145 u ahead. The playable band ends at 138, which put the old
   300 u plane's edge inside that — and a hard horizontal seam across the screen is what you
   get when abyss-dark floor stops against the clear colour. */
const SPAN_Z = 460;
const CENTRE_Z = 92;
const SEG_X = 330;
const SEG_Z = 184;

/* The field is compiled into BOTH stages. The vertex stage displaces the mesh with it and the
   fragment stage differentiates it for the normal; if the two ever disagree the lighting
   visibly slides off the geometry as the camera moves. */
const FIELD = COAST_GLSL;

const VERT = /* glsl */ `
uniform float uDuneAmp;
uniform float uReefRise;

varying vec3  vWorld;
varying float vDepth;

${LURE_GLSL}
${FIELD}

void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  // bedY is an absolute world height, not an offset — the plane's own Y is irrelevant.
  world.y = bedY(world.xz, uDuneAmp, uReefRise);

  vec4 mv = viewMatrix * world;
  vWorld = world.xyz;
  vDepth = -mv.z;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */ `
uniform float uTime;
uniform vec3  uSandHi;
uniform vec3  uSandLo;
uniform vec3  uSandDry;
uniform vec3  uSandWet;
uniform vec3  uLightDir;   // unit vector pointing TOWARD the sun; usable directly as L
uniform vec3  uSunColor;
uniform vec3  uAmbTop;
uniform vec3  uAmbBottom;
uniform vec3  uFogColor;
uniform vec3  uAbyss;
uniform float uFogDensity;
uniform float uDuneAmp;
uniform float uReefRise;
uniform float uSurface;
uniform float uRippleAmp;
uniform float uRippleLen;
uniform float uCaustic;
uniform vec3  uRockLo;
uniform vec3  uRockHi;
uniform vec3  uCoralline;
uniform vec3  uTurf;
uniform sampler2D uShadowMap;
uniform mat4  uShadowMatrix;
uniform float uShadowStrength;

varying vec3  vWorld;
varying float vDepth;

${LURE_GLSL}
${FIELD}

float reefMaskW(vec2 p) { return reefMaskAt(p, p.y + shoreShift(p.x)); }

/* Current ripples.

   Per-fragment context, set once at the top of main(). The warp and the patch mask vary over
   5-50 u, so they are effectively constant across the 0.35 u finite-difference stencil that
   relief() uses for its normal — computing them once rather than at all four taps costs
   nothing in accuracy and saves eighteen noise lookups a pixel. */
vec2  gWarp;
float gPatch;
vec2  gD1;
vec2  gD2;

void rippleSetup(vec2 p) {
  // POSITION warp, not phase. Adding to the phase slides a crest along itself and leaves it
  // dead straight; displacing the sample point is what actually bends the crest line. Two
  // scales, and both gradients stay under 1 so the field never folds back on itself.
  vec2 w1 = vec2(vnoise(p * 0.055),        vnoise(p * 0.055 + 31.0)) - 0.5;
  vec2 w2 = vec2(vnoise(p * 0.185 + 7.0),  vnoise(p * 0.185 + 53.0)) - 0.5;
  gWarp = w1 * 14.0 + w2 * 3.4;

  // Ripples come in FIELDS. Real sand is worked into ripple in some places and left smooth in
  // others, and an unbroken crest field over the whole shelf is a corrugated roof no matter
  // how fine or how warped it is. Multiplied by a shoaling term as well: ripples are made by
  // wave orbitals reaching the bed, so they die out as the water gets deep.
  float shoal = 1.0 - smoothstep(9.0, 21.0, uSurface - bedY(p, uDuneAmp, uReefRise));
  gPatch = smoothstep(0.34, 0.74, vnoise(p * 0.021 + 11.0)) * (0.28 + 0.72 * shoal);

  // The crest DIRECTION drifts. Keep this SMALL: a rotating crest field has topological
  // defects wherever the direction winds, and past ~0.6 rad of swing they dominate as
  // fingerprint whorls. The two sets are held near-perpendicular instead, so they cross
  // rather than reinforce into one grain.
  float dir = (vnoise(p * 0.0065) - 0.5) * 0.55;
  gD1 = vec2(cos(dir), sin(dir));
  gD2 = vec2(cos(dir + 1.35), sin(dir + 1.35));
}

float ripple(vec2 p) {
  vec2 q = p + gWarp;
  float k = 6.2831853 / uRippleLen;
  float a = sin(dot(q, gD1) * k + uTime * 0.16);
  float b = sin(dot(q, gD2) * k * 0.47 - uTime * 0.11);
  float s = (a * 0.66 + b * 0.34) * 0.5 + 0.5;
  // Gently biased toward flat troughs and rounded crests, which is how sand sits. A plain
  // s*s doubles the harmonic content, and that extra harmonic is what reads as corrugation.
  return (pow(s, 1.45) * 2.0 - 1.0) * gPatch;
}

// Ripples are a sand structure. Suppressing them over the rock is most of what stops the
// reef looking like a dune with a different paint job.
float relief(vec2 p, float detail) {
  return bedY(p, uDuneAmp, uReefRise)
       + ripple(p) * uRippleAmp * detail * (1.0 - reefMaskW(p) * 0.88);
}

float lattice(vec2 p) {
  return (sin(p.x) + sin(p.y * 0.87 + 1.7) + sin((p.x + p.y) * 0.62 + 3.1)) * 0.3333333;
}

vec2 rot2(vec2 p, float a) {
  float c = cos(a), s = sin(a);
  return vec2(p.x * c - p.y * s, p.x * s + p.y * c);
}

float caustics(vec2 w, float t) {
  // Cell size. The raw lattice is ~11 u at world scale; this is the knob that sets how far
  // apart the filaments run. Tight enough and they stop reading as focused light and start
  // reading as a texture printed on the sand.
  w *= 1.50;
  // Domain warp. Summed sines are strictly periodic, so no amount of layering stops them
  // reading as a weave; displacing the sample point by a slow noise field is what destroys
  // the repeat. Two scales — the coarse one moves whole filaments, the fine one frays them.
  vec2 warp = vec2(vnoise(w * 0.085 + vec2(0.0, t * 0.021)),
                   vnoise(w * 0.085 + vec2(19.3, -t * 0.017))) - 0.5;
  vec2 fray = vec2(vnoise(w * 0.31 + vec2(41.0, t * 0.043)),
                   vnoise(w * 0.31 + vec2(-7.9, -t * 0.038))) - 0.5;
  w += warp * 11.0 + fray * 2.2;
  // Three layers at incommensurate scales AND rotations; min() keeps only where all three
  // are near a zero crossing, which yields sparse filaments instead of a plaid.
  float a = 1.0 - abs(lattice(rot2(w, 0.00) * 0.55 + vec2( 0.31, -0.22) * t));
  float b = 1.0 - abs(lattice(rot2(w, 2.09) * 1.27 + vec2(-0.19,  0.42) * t));
  float c = 1.0 - abs(lattice(rot2(w, 4.19) * 0.83 + vec2( 0.07,  0.29) * t));
  return pow(clamp(min(min(a, b), c), 0.0, 1.0), 4.2);
}

/* Single-cascade shadow lookup. The light camera is orthographic, so light-space depth is
   linear in NDC and a plain compare works. 4-tap PCF is enough: the sun is high and the
   receiver near-flat, and anything softer loses the crisp voxel silhouettes. */
float shadowAt(vec3 world, float ndl) {
  vec4 lp = uShadowMatrix * vec4(world, 1.0);
  vec3 c = lp.xyz / lp.w;
  c = c * 0.5 + 0.5;
  if (c.x < 0.0 || c.x > 1.0 || c.y < 0.0 || c.y > 1.0 || c.z > 1.0) return 1.0;
  float bias = mix(0.0016, 0.0003, clamp(ndl, 0.0, 1.0));
  float texel = 1.0 / 2048.0;
  float lit = 0.0;
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel,  texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel,  texel)).r);
  lit *= 0.25;
  vec2 e = abs(c.xy - 0.5) * 2.0;
  float edge = 1.0 - smoothstep(0.80, 1.0, max(e.x, e.y));
  return mix(1.0, lit, edge);
}

void main() {
  vec2 p = vWorld.xz;

  // Fade the fine ripple out with range. At this pitch the far half of the screen is
  // sub-pixel and would shimmer, and fog is nowhere near thick enough to hide it.
  float detail = 1.0 - smoothstep(40.0, 120.0, vDepth);

  // must run before any relief()/ripple() call — they read the globals it sets
  rippleSetup(p);

  float e = 0.35;
  float h0 = relief(p, detail);
  float hx = relief(p + vec2(e, 0.0), detail);
  float hz = relief(p + vec2(0.0, e), detail);
  vec3 n = normalize(vec3((h0 - hx) / e, 1.0, (h0 - hz) / e));

  float m = reefMaskW(p);
  // Height relative to the sea surface: positive is dry land, negative is depth.
  float above = vWorld.y - uSurface;

  float tone = vnoise(p * 0.115) * 0.5 + mix(0.5, vnoise(p * 0.34), detail) * 0.5;
  vec3 sand = mix(uSandLo, uSandHi, smoothstep(0.08, 0.94, tone));
  // Crests scatter more than troughs; without this the ripples only exist in the shading.
  sand *= 1.0 + 0.11 * clamp(ripple(p) * 1.2, -1.0, 1.0) * detail * (1.0 - m * 0.88);

  /* The waterline. Wet sand is darker and flatter than dry because the film of water fills
     the pores and kills the diffuse scatter — this one band is most of what makes a beach
     read as a beach rather than as terrain that happens to stop. The dry side is pushed
     brighter and warmer, and both transitions are ~1 u so the tide edge stays crisp. */
  sand = mix(sand, uSandWet, smoothstep(-2.6, 0.15, above) * 0.85);
  sand = mix(sand, uSandDry, smoothstep(0.15, 1.30, above));

  // Reef rock, kept deliberately neutral and a good deal darker than the sand. Saturated
  // stone competes with the coral standing on it and the whole patch reads as mud; the
  // contrast against bright sand is what makes the patch a place rather than a stain.
  vec3 rock = mix(uRockLo, uRockHi, smoothstep(0.22, 0.82, vnoise(p * 0.20 + 7.0)));
  rock = mix(rock, uCoralline, smoothstep(0.54, 0.94, vnoise(p * 0.47 + 23.0)) * 0.55);
  rock = mix(rock, uTurf, smoothstep(0.56, 0.96, vnoise(p * 0.33 + 61.0)) * 0.45 * detail);
  // Hollows in the rugosity hold silt and shade; the same field that lifted them darkens them.
  rock *= 1.0 - clamp(-reefRugosity(p), 0.0, 1.0) * 0.26;
  sand = mix(sand, rock, m);

  float ndl = dot(n, uLightDir);
  // Shadow attenuates the DIRECT term only; ambient still fills, which is what keeps a
  // shadow underwater reading as blue shade rather than a black hole.
  float shade = mix(1.0, shadowAt(vWorld, ndl), uShadowStrength);

  float key = ndl * 0.5 + 0.5;
  key *= key;
  vec3 ambient = mix(uAmbBottom, uAmbTop, n.y * 0.5 + 0.5);
  vec3 col = sand * (ambient + uSunColor * key * shade);

  // Caustics land on the sand in world XZ, so they slide across the floor correctly as the
  // camera moves. Gated by the shadow too — light that never reached the sand cannot have
  // been focused into a caustic on it — and by depth, because the surface has less light
  // left to focus the further down it has to send it.
  float lit = smoothstep(0.0, 0.55, ndl);
  float reach = (1.0 - smoothstep(6.0, 26.0, -above)) * (1.0 - smoothstep(-0.4, 0.6, above));
  col += uSunColor * (caustics(p, uTime) * uCaustic * lit * shade * reach * (0.35 + 0.65 * detail));

  /* The drop-off. Beyond the fore-reef the bed falls away faster than fog can hide it, so
     without this the cliff face stays sand-coloured all the way down and reads as a wall
     rather than as deep water. Extinction is a function of DEPTH, and it is separate from —
     and applied before — the distance fog. */
  col = mix(col, uAbyss, smoothstep(10.0, 46.0, -above));

  /* The lure, BEFORE the fog. It is a light IN the water, so the same haze has to attenuate
     it — added after the mix it burns through and reads as a decal on the screen. The sand's
     normal is close enough to up for this; the bed's real slope is gentle. */
  col += lureLight(vWorld, vec3(0.0, 1.0, 0.0)) * 0.5;

  float fd = vDepth * uFogDensity;
  col = mix(col, uFogColor, 1.0 - exp(-fd * fd));

  // Linear out. The renderer owns tone mapping and the sRGB transfer.
  gl_FragColor = vec4(col, 1.0);
}
`;

export class Seafloor {
  readonly mesh: THREE.Mesh;

  private readonly geometry: THREE.PlaneGeometry;
  private readonly material: THREE.ShaderMaterial;
  /** quad size along X; the window snaps to this so vertices never crawl */
  private readonly stepX = SPAN_X / SEG_X;

  constructor() {
    this.geometry = new THREE.PlaneGeometry(SPAN_X, SPAN_Z, SEG_X, SEG_Z);
    // Bake the lay-flat rotation into the vertices so the mesh matrix stays a translation and
    // modelMatrix * position is literally the world position the shader wants.
    this.geometry.rotateX(-Math.PI / 2);
    this.geometry.translate(0, 0, CENTRE_Z);
    // The vertex stage moves geometry to absolute heights spanning roughly -30 to +37, so the
    // bounds have to cover that or the floor pops out of the frustum over a dune.
    this.geometry.boundingSphere = new THREE.Sphere(
      new THREE.Vector3(0, 4, CENTRE_Z),
      Math.hypot(SPAN_X, SPAN_Z) * 0.5 + 60,
    );

    const bone = hexToLinear(TOKENS.bone);
    const hydro = hexToLinear(TOKENS.hydro);
    // Sand is `bone` pulled a little toward `corrode`. Both are art-direction tokens, so the
    // shelf stays inside the palette while actually reading as sand rather than as lit water.
    const sand = mixRgb(bone, hexToLinear(TOKENS.corrode), 0.10);
    const sandDeep = mixRgb(bone, hexToLinear(TOKENS.corrode), 0.20);

    this.material = new THREE.ShaderMaterial({
      // Named so a compile failure says WHICH shader; three logs the material name and
      // nothing else identifying, and an empty name means reading every shader in the project.
      name: 'Seafloor',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        ...lureUniforms(),
        uTime: { value: 0 },
        uSandHi: { value: v3(shade(sand, 0.95)) },
        uSandLo: { value: v3(mixRgb(shade(sandDeep, 0.46), hydro, 0.12)) },
        // Dry sand is brighter and warmer; wet sand is darker, because the water film fills
        // the pores and kills the diffuse scatter that makes dry sand pale.
        uSandDry: { value: v3(shade(mixRgb(bone, hexToLinear(TOKENS.corrode), 0.06), 1.12)) },
        uSandWet: { value: v3(mixRgb(shade(sandDeep, 0.34), hydro, 0.22)) },
        uLightDir: { value: sunLightDir() },
        uSunColor: { value: v3(hexToLinear(WATER.sunColor)) },
        uAmbTop: { value: v3(hexToLinear(WATER.ambientTop)) },
        uAmbBottom: { value: v3(hexToLinear(WATER.ambientBottom)) },
        uFogColor: { value: v3(hexToLinear(WATER.fogColor)) },
        uAbyss: { value: v3(hexToLinear(TOKENS.abyss)) },
        uFogDensity: { value: WATER.fogDensity },
        uDuneAmp: { value: OCEAN.duneAmplitude },
        uReefRise: { value: OCEAN.reefRise },
        uSurface: { value: OCEAN.surfaceHeight },
        // Reef substrate. Neutral grey-green stone, a pale worn top, and two mottles:
        // coralline algae (the dull pink crust that cements a reef) and turf.
        uRockLo: { value: v3(hexToLinear(0x2c322e)) },
        uRockHi: { value: v3(hexToLinear(0x6d6b5e)) },
        uCoralline: { value: v3(hexToLinear(0x8a5a5e)) },
        uTurf: { value: v3(hexToLinear(0x3c4a30)) },
        uRippleAmp: { value: OCEAN.rippleAmplitude },
        uRippleLen: { value: OCEAN.rippleWavelength },
        // Caustics are additive on top of an already-lit surface. Much past this the sand
        // tone disappears under them and the player stops reading as the brightest thing.
        uCaustic: { value: 0.20 },
        uShadowMap: { value: null as THREE.Texture | null },
        uShadowMatrix: { value: new THREE.Matrix4() },
        // 0 until a shadow pass is attached, so the floor renders correctly on its own.
        uShadowStrength: { value: 0 },
      },
      fog: false,          // this shader does its own exp2 fog; three's chunks would double it
      lights: false,
      transparent: false,
      depthWrite: true,
      side: THREE.FrontSide,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = 'seafloor';
    this.mesh.matrixAutoUpdate = false;
    this.mesh.updateMatrix();
  }

  /**
   * @param followX the player's X. The window slides with it, snapped to whole quads —
   *                an unsnapped slide makes every vertex crawl through the field, which
   *                shimmers far worse than the terrain moving in discrete steps ever could.
   */
  update(time: number, followX: number): void {
    this.material.uniforms.uTime.value = time;
    const snapped = Math.round(followX / this.stepX) * this.stepX;
    if (this.mesh.position.x !== snapped) {
      this.mesh.position.x = snapped;
      this.mesh.updateMatrix();
    }
  }


  /** the anglerfish's lure, the one point light in the game; see render/LureLight.ts */
  setLure(lure: LureState): void {
    applyLure(this.material.uniforms, lure);
  }

  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.material.uniforms, env);
  }

  /** the sky drives the caustic strength too: no sun overhead, nothing to focus */
  setCaustic(k: number): void {
    this.material.uniforms.uCaustic.value = 0.20 * k;
  }

  /** point the floor at a shadow pass; `strength` 0..1 fades the whole effect */
  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    const u = this.material.uniforms;
    u.uShadowMap.value = map;
    (u.uShadowMatrix.value as THREE.Matrix4).copy(matrix);
    u.uShadowStrength.value = map === null ? 0 : strength;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
